import { CookieInformationDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';

import OneWebStoreIdCookieInformationService from '../../services/cookie-information/OneWebStoreIdCookieInformationService.js';
import OneWebZipCodeCookieInformationService from '../../services/cookie-information/OneWebZipCodeCookieInformationService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import type ICookieInformationServiceOptions from './ICookieInformationServiceOptions.js';

/**
 * This class is used as a simple interface for cookie consent services.
 *
 * @deprecated Use OneWebStoreIdCookieInformationService or OneWebZipCodeCookieInformationService instead.
 */
export default class CookieInformationService {
	private readonly dexfConfiguration: DexfConfiguration;
	private readonly cache: CacheStore;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 */
	constructor(options: ICookieInformationServiceOptions) {
		this.dexfConfiguration = new DexfConfiguration(options);
		this.cache = options.cache || new GlobalCacheStore();
	}

	/**
	 * Fetches and returns store id selected by the user on the web.
	 *
	 * @returns Promise with store id.
	 */
	public async getStoreId(): Promise<string | null> {
		const service = await this.getStoreIdService();
		return await service.getStoreId();
	}

	/**
	 * Fetches and returns zip code set by the user on the web.
	 *
	 * @returns Promise with store id.
	 */
	public async getZipCode(): Promise<string | null> {
		const service = await this.getZipCodeService();
		return await service.getZipCode();
	}

	/**
	 * Returns service.
	 *
	 * @returns Service.
	 */
	private async getStoreIdService(): Promise<OneWebStoreIdCookieInformationService> {
		const settings = await this.dexfConfiguration.getSettings();

		if (
			settings.kompis.serviceSettings.cookieInformationDataSource ===
			CookieInformationDataSourceEnum.disabled
		) {
			throw new ServiceException(
				`The cookieInformationDataSource is set to "Disabled". When it is, it is not possible to fetch the cookie information on the market.`
			);
		}

		return new OneWebStoreIdCookieInformationService({
			endpoint: settings.kompis.endpoints.oneWebCookieInformationService,
			cacheStore: this.cache
		});
	}

	/**
	 * Returns service.
	 *
	 * @returns Service.
	 */
	private async getZipCodeService(): Promise<OneWebZipCodeCookieInformationService> {
		const settings = await this.dexfConfiguration.getSettings();

		if (
			settings.kompis.serviceSettings.cookieInformationDataSource ===
			CookieInformationDataSourceEnum.disabled
		) {
			throw new ServiceException(
				`The cookieInformationDataSource is set to "Disabled". When it is, it is not possible to fetch the cookie information on the market.`
			);
		}

		return new OneWebZipCodeCookieInformationService({
			endpoint: settings.kompis.endpoints.oneWebCookieInformationService,
			cacheStore: this.cache
		});
	}
}
